'use client';

import { usePathname, useRouter } from 'next/navigation';
import React, { createContext, useContext, useEffect, useState } from 'react';

import { type Locale, i18n, localeUrls } from '@/config/i18n';
import { de } from '@/translations/de';
import { en } from '@/translations/en';
import { tr } from '@/translations/tr';

type Translation = typeof tr;

interface LanguageContextType {
  t: Translation;
  locale: Locale;
  setLocale: (locale: Locale) => void;
  localeUrls: Record<string, string>;
}

const translations = {
  tr,
  en,
  de,
};

// Blog yazıları için URL eşleştirmeleri
const blogUrlMappings = {
  'botoks-fiyatlari-2026': {
    tr: 'botoks-fiyatlari-2026',
    en: 'botox-prices-2026',
    de: 'botox-preise-2026',
  },
  'botoks-nedir-nasil-yapilir': {
    tr: 'botoks-nedir-nasil-yapilir',
    en: 'what-is-botox-how-is-it-done',
    de: 'was-ist-botox-wie-wird-es-gemacht',
  },
  'alin-botoksu-nedir': {
    tr: 'alin-botoksu-nedir',
    en: 'forehead-botox-what-is-it',
    de: 'stirn-botox-was-ist-das',
  },
  'kaz-ayagi-botoksu-nedir': {
    tr: 'kaz-ayagi-botoksu-nedir',
    en: 'crows-feet-botox-what-is-it',
    de: 'kraehenfuesse-botox-was-ist-das',
  },
  'botoks-yan-etkileri': {
    tr: 'botoks-yan-etkileri',
    en: 'botox-side-effects',
    de: 'botox-nebenwirkungen',
  },
  'botoks-dolgu-farklari': {
    tr: 'botoks-dolgu-farklari',
    en: 'botox-filler-differences',
    de: 'botox-fueller-unterschiede',
  },
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const [locale, setLocaleState] = useState<Locale>(() => {
    // URL'den dil kodunu al
    const pathLocale = pathname.split('/')[1] as Locale;
    return i18n.locales.includes(pathLocale) ? pathLocale : i18n.defaultLocale;
  });

  useEffect(() => {
    // URL'den dil kodunu al
    const pathLocale = pathname.split('/')[1] as Locale;
    if (i18n.locales.includes(pathLocale)) {
      setLocaleState(pathLocale);
    } else {
      // Eğer URL'de dil kodu yoksa varsayılan dile yönlendir
      const newPath = `/${i18n.defaultLocale}${pathname}`;
      if (pathname !== newPath) {
        router.push(newPath);
      }
    }
  }, [pathname, router]);

  const setLocale = (newLocale: Locale) => {
    if (newLocale === locale) return; // Aynı dile geçiş yapılmaya çalışılıyorsa işlemi durdur
    
    setLocaleState(newLocale);
    
    // Mevcut path'i al
    const pathParts = pathname.split('/');
    const currentLocale = pathParts[1] as Locale;
    const pathWithoutLocale = pathParts.slice(2).join('/');
    
    // Eğer path boşsa ana sayfaya yönlendir
    if (!pathWithoutLocale) {
      router.push(`/${newLocale}`);
      return;
    }

    // Blog yazısı kontrolü - spesifik blog yazıları için mapping'leri kontrol et
    if (pathname.includes('/blog/') && pathname !== `/${currentLocale}/blog`) {
      const blogSlug = pathname.split('/blog/')[1];
      
      // Blog mapping'lerini kontrol et
      for (const [key, mapping] of Object.entries(blogUrlMappings)) {
        if (mapping[currentLocale] === blogSlug) {
          // Yeni dildeki karşılığını bul
          const newSlug = mapping[newLocale];
          if (newSlug) {
            router.push(`/${newLocale}/blog/${newSlug}`);
            return;
          }
          break;
        }
      }
      
      // Eğer spesifik mapping bulunamadıysa blog ana sayfasına yönlendir
      router.push(`/${newLocale}/blog`);
      return;
    }

    // URL eşleştirmelerini kontrol et - tam URL ile karşılaştır
    // Trailing slash'i normalize et (next.config.js'de trailingSlash: true olduğu için)
    const normalizedPath = pathname.endsWith('/') && pathname !== '/' 
      ? pathname.slice(0, -1) 
      : pathname;
    const currentFullPath = normalizedPath; // Normalize edilmiş path

    // URL'leri localeUrls'den al
    const currentUrls = localeUrls[currentLocale];
    const newUrls = localeUrls[newLocale];

    // Mevcut path'in hangi URL'ye ait olduğunu bul
    for (const [key, url] of Object.entries(currentUrls)) {
      if (url === currentFullPath) {
        // Yeni dildeki karşılığını bul
        const newFullPath = newUrls[key];
        if (newFullPath) {
          router.push(newFullPath);
          return;
        }
        break;
      }
    }

    // Eğer tam eşleşme bulunamadıysa, kısmi eşleşme dene
    // Bu alt sayfalar için gerekli (örn: /en/aesthetic-surgery/facial-aesthetics)
    for (const [key, url] of Object.entries(currentUrls)) {
      if (currentFullPath.startsWith(url + '/') || currentFullPath === url) {
        const newFullPath = newUrls[key];
        if (newFullPath) {
          // Alt sayfa varsa o kısmı koru
          const remainingPath = currentFullPath.replace(url, '');
          router.push(newFullPath + remainingPath);
          return;
        }
      }
    }

    // Eğer hiçbir eşleşme bulunamadıysa mevcut path'i koru (fallback)
    router.push(`/${newLocale}/${pathWithoutLocale}`);
  };

  const value = {
    t: translations[locale],
    locale,
    setLocale,
    localeUrls: localeUrls[locale],
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
} 